import { createAsyncThunk } from '@reduxjs/toolkit';
import ListingRegistrationService from '@/services/ListingRegistrationServices';
import ContactsService from '@/services/ContactsService';
import PropertyDescriptionService from '@/services/PropertyDescriptionService';
import DevelopmentsService from '@/services/DevelopmentsService';

// Not using the handler wrapper because it needs direct service access for the restore loop
export const unlinkAndRestoreTags = createAsyncThunk(
  'ListingRegistration/unlinkAndRestoreTags',
  async ({
    propertyId,
    developmentId,
    currentAmenities,
    developmentTagsSnapshot,
    propertyTagsSnapshot,
  }) => {
    await ListingRegistrationService.unlinkPropertyFromDevelopment(propertyId);

    const toAddToDev = developmentTagsSnapshot.filter(
      (id) => !currentAmenities.includes(id)
    );
    const toRemoveFromDev = currentAmenities.filter(
      (id) => !developmentTagsSnapshot.includes(id)
    );
    const toAddToProp = propertyTagsSnapshot.filter(
      (id) => !currentAmenities.includes(id)
    );
    const toRemoveFromProp = currentAmenities.filter(
      (id) => !propertyTagsSnapshot.includes(id)
    );

    await Promise.all([
      ...toAddToDev.map((tagId) =>
        ListingRegistrationService.setDevelopmentTag({
          developmentId,
          tagId,
          action: 'add',
        })
      ),
      ...toRemoveFromDev.map((tagId) =>
        ListingRegistrationService.setDevelopmentTag({
          developmentId,
          tagId,
          action: 'remove',
        })
      ),
      ...toAddToProp.map((tagId) =>
        ListingRegistrationService.setPropertyTag({
          propertyId,
          tagId,
          action: 'add',
        })
      ),
      ...toRemoveFromProp.map((tagId) =>
        ListingRegistrationService.setPropertyTag({
          propertyId,
          tagId,
          action: 'remove',
        })
      ),
    ]);
  }
);

const handler = (name, asyncFunction) =>
  createAsyncThunk(`ListingRegistration/${name}`, async (arg) => {
    const data = await asyncFunction(arg);
    return data;
  });

export const checkHasDevelopments = handler(
  'checkHasDevelopments',
  DevelopmentsService.getCompanyDevelopments
);

export const getContactsBySearch = handler(
  'getContactsBySearch',
  ListingRegistrationService.getContactsBySearch
);

export const fetchPrefillOwnerContact = handler(
  'fetchPrefillOwnerContact',
  ContactsService.getContact
);

export const createOwner = handler(
  'createOwner',
  ContactsService.createContact
);

export const createProperty = handler(
  'createProperty',
  ListingRegistrationService.createProperty
);

export const fetchCharacteristicsSchema = handler(
  'fetchCharacteristicsSchema',
  ListingRegistrationService.getCharacteristicsSchema
);

export const fetchDevelopmentTags = handler(
  'fetchDevelopmentTags',
  ListingRegistrationService.getPropertyTags
);

export const setDevelopmentTag = handler(
  'setDevelopmentTag',
  ListingRegistrationService.setDevelopmentTag
);

export const setPropertyTag = handler(
  'setPropertyTag',
  ListingRegistrationService.setPropertyTag
);

export const linkPropertyToDevelopment = handler(
  'linkPropertyToDevelopment',
  ListingRegistrationService.linkPropertyToDevelopment
);

export const unlinkPropertyFromDevelopment = handler(
  'unlinkPropertyFromDevelopment',
  ListingRegistrationService.unlinkPropertyFromDevelopment
);

export const saveConstructionDate = handler(
  'saveConstructionDate',
  ListingRegistrationService.saveConstructionDate
);

export const fetchPropertyTags = handler(
  'fetchPropertyTags',
  ListingRegistrationService.getPropertyActiveTags
);

export const fetchAmenitiesSchema = handler(
  'fetchAmenitiesSchema',
  ListingRegistrationService.getAmenitiesSchema
);

export const fetchCotizationSchema = handler(
  'fetchCotizationSchema',
  ListingRegistrationService.getCotizationSchema
);

export const fetchListingSupervision = handler(
  'fetchListingSupervision',
  ListingRegistrationService.getListingSupervision
);

export const saveControlListingInfoBanner = handler(
  'saveControlListingInfoBanner',
  ListingRegistrationService.postUserPreferences
);

export const fetchPricePosition = handler(
  'fetchPricePosition',
  ListingRegistrationService.getPricePosition
);

export const generateAIDescription = handler(
  'generateAIDescription',
  PropertyDescriptionService.generateDescription
);

export const getPlanUpgradeStatus = handler(
  'getPlanUpgradeStatus',
  PropertyDescriptionService.getIfPlanUpgradeWasRequested
);

export const requestFullPlan = handler(
  'requestFullPlan',
  PropertyDescriptionService.requestFullPlan
);

export const fetchProperty = handler(
  'fetchProperty',
  ListingRegistrationService.getProperty
);

export const updateProperty = handler(
  'updateProperty',
  ListingRegistrationService.updateProperty
);
